import { useState } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { Box, useTheme, IconButton, Tooltip } from '@mui/material';
import { useNodeId, useReactFlow, useStore } from '@xyflow/react';
import { useTranslate } from 'react-admin';

import { openResourceInNewTab } from 'src/lib/openResourceInNewTab';

import IconWrapper from '../components/IconWrapper';

import { getControlsComponentName } from './NodeControls';

import type { ReactFlowState } from '@xyflow/react';

interface NodeHeaderProps {
  icon: React.ReactNode;
  title?: string | null;
  condensed?: boolean;
  referencedResource?: {
    resource: string;
    id: string;
  };
  description?: string;
}

const selector = (s: ReactFlowState): { isInteractive: boolean; isDragging: boolean } => {
  const nodes = Array.from(s.nodeLookup.values());
  return {
    isInteractive: s.nodesDraggable || s.nodesConnectable || s.elementsSelectable,
    isDragging: nodes.some((node) => node.dragging),
  };
};

const NodeHeader = ({
  icon,
  title = null,
  condensed = false,
  referencedResource = undefined,
  description = undefined,
}: NodeHeaderProps): React.ReactElement => {
  const reactFlowInstance = useReactFlow();
  const theme = useTheme();
  const nodeId = useNodeId();
  const translate = useTranslate();
  const currentNode = reactFlowInstance?.getNode(nodeId ?? '');
  const { isInteractive, isDragging } = useStore(selector);
  const descriptionFromStore = useStore((state) => {
    if (nodeId === null) {
      return undefined;
    }
    const node = state.nodeLookup.get(nodeId);
    const data = node?.data;
    return typeof data?.description === 'string' ? data.description : undefined;
  });
  const [isHovered, setIsHovered] = useState(false);
  const [isNodeHovered, setIsNodeHovered] = useState(false);
  const trimmedDescriptionFromStore = typeof descriptionFromStore === 'string' ? descriptionFromStore.trim() : '';
  const trimmedDescriptionFromProp = typeof description === 'string' ? description.trim() : '';
  const trimmedDescription = [trimmedDescriptionFromStore, trimmedDescriptionFromProp]
    .filter((value) => value.length > 0)
    .join('\n');
  const hasDescription = trimmedDescription.length > 0;

  const handleViewResource = (): void => {
    if (referencedResource) {
      openResourceInNewTab(referencedResource.resource, referencedResource.id);
    }
  };

  const showEyeIcon = referencedResource && isHovered;
  const showEditIcon =
    condensed &&
    isNodeHovered &&
    isInteractive &&
    !isDragging &&
    currentNode?.type !== undefined &&
    getControlsComponentName(currentNode.type) !== false;

  return (
    <Box
      display='flex'
      justifyContent='space-between'
      alignItems='center'
      gap={1}
      // Condensed nodes are just the icon badge. Normal nodes keep a 52px header
      // (6px + 40px icon + 6px), which NodeBody's handle offsets rely on.
      padding={condensed ? 0 : theme.spacing(0.75, 1)}
      sx={condensed ? { position: 'relative' } : undefined}
      onMouseEnter={() => {
        setIsNodeHovered(true);
      }}
      onMouseLeave={() => {
        setIsNodeHovered(false);
      }}
    >
      <Box display='flex' justifyContent='flex-start' alignItems='center' minWidth={0} gap={1.5}>
        <Box
          onMouseEnter={() => {
            referencedResource && setIsHovered(true);
          }}
          onMouseLeave={() => {
            setIsHovered(false);
          }}
          onClick={showEyeIcon === true && !showEditIcon ? handleViewResource : undefined}
          sx={{
            cursor: showEyeIcon === true || showEditIcon ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconWrapper size={condensed ? 48 : 40}>
            {showEditIcon ? (
              <IconButton
                size='small'
                aria-label='open settings'
                className='node-open-settings'
                data-testid='node-open-settings'
                data-node-open-settings
                sx={{ p: 0, color: 'inherit' }}
              >
                <SettingsRoundedIcon />
              </IconButton>
            ) : showEyeIcon === true ? (
              <VisibilityRoundedIcon />
            ) : (
              icon
            )}
          </IconWrapper>
        </Box>
        {title !== null && title !== undefined && (
          <Box display='flex' alignItems='center' gap={0.5} minWidth={0}>
            {hasDescription ? (
              <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{trimmedDescription}</Box>}>
                <IconButton
                  size='small'
                  aria-label={translate('callflows.interface.description')}
                  sx={{ p: 0.25, color: 'inherit' }}
                >
                  <InfoOutlinedIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            ) : null}
            <Tooltip title={title} placement='top'>
              <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600 }}>
                {title}
              </Box>
            </Tooltip>
          </Box>
        )}
      </Box>
      {isInteractive && !condensed && (
        <>
          {currentNode?.type !== undefined && getControlsComponentName(currentNode.type) !== false && (
            <Box
              justifyContent='flex-end'
              className='node-controls-wrapper'
              sx={{
                // Kept in the layout flow so the header does not resize when it appears on hover.
                display: 'flex',
                visibility: 'hidden',
              }}
            >
              <IconButton
                size='small'
                aria-label='open settings'
                className='node-open-settings'
                data-testid='node-open-settings'
                data-node-open-settings
                // Smaller than the 40px icon, so the header height does not depend on
                // the presence of the controls. NodeBody handle offsets rely on that.
                sx={{ p: 0.5, color: 'inherit' }}
              >
                <SettingsRoundedIcon />
              </IconButton>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default NodeHeader;
